import React from 'react';
import Module from 'Components/Core/Module';
import MarketdataGroupSelector from './MarketdataGroupSelector';
import MarketdataChartTile from './MarketdataChartTile';
import MarketdataRangeSelector, { CHART_RANGES } from './MarketdataRangeSelector';
import { setMarketdataForcedGroup, useMarketdataForcedGroup } from './marketdataForcedGroup';
import './Marketdata.css';

// Charts-Modul: dieselben Watchlists wie "Watchlist Live", aber als
// Kachel-Raster aus Mini-Charts statt einer Tabelle - ein Chart pro Symbol,
// gleiche Reihenfolge (sortorder) und gleiche Groesse wie bisher im
// Detail-Panel des Live-Moduls. Anders als "Watchlist Live" nicht live
// gepollt (Chart-Kerzen aendern sich nicht Sekunde fuer Sekunde relevant),
// sondern einmalig beim Oeffnen/Gruppenwechsel geladen (analog
// MarketdataChart.inc fuers Detail-Panel).
export default function MarketdataCharts(props) {

	const x = props.x;
	const [charts, setCharts] = React.useState([]);
	const [loading, setLoading] = React.useState(false);
	const [error, setError] = React.useState('');
	const [selectedGroupId, setSelectedGroupId] = React.useState(null);
	const [range, setRange] = React.useState('1T');
	// Erzwungener Watchlist-Wechsel von aussen (siehe marketdataForcedGroup.js) -
	// "Charts anzeigen"-Icon in MarketdataWatchlist.js setzt das, BEVOR dieses
	// Modul aktiviert wird.
	const [forcedGroup, setForcedGroup] = React.useState(null);
	const consumeForcedGroup = useMarketdataForcedGroup(x, setForcedGroup);

	function loadCharts() {
		if (selectedGroupId === null) {
			return;
		}
		setLoading(true);
		setError('');
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataCharts', { groupid: selectedGroupId, range }, (result) => {
			setLoading(false);
			if (result.data.returncode !== 1) {
				setError(result.data.content?.message || 'Charts konnten nicht geladen werden.');
				setCharts([]);
				return;
			}
			setCharts(result.data.content || []);
		});
	}

	React.useEffect(() => {
		loadCharts();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGroupId, range]);

	// Charts live nachziehen statt nur beim Oeffnen/Wechseln einmalig zu laden -
	// nutzt denselben Socket.IO-Kanal wie "Watchlist Live" (siehe
	// MarketdataQuotes.js). Bewusst NUR fuer "1m" (siehe gleichnamige Konstante
	// in MarketdataDetailPanel.js) - bei "1T" (5-Minuten-Kerzen) erzeugte das
	// Anhaengen jedes einzelnen Live-Ticks eine Aufloesungs-Mismatch zur
	// groben historischen 5-Minuten-Reihe und verzerrte den Chart-Verlauf.
	const isLiveAppendable = range === '1m';
	React.useEffect(() => {
		if (!isLiveAppendable) {
			return;
		}
		function handleQuote(args) {
			// Nur verarbeiten, wenn "Charts" gerade das aktive/sichtbare Modul ist
			// (analog MarketdataQuotes.js) - unnoetige Re-Renders eines
			// unsichtbaren Kachel-Rasters vermeiden. Beim naechsten Aktivieren
			// laedt loadCharts() ohnehin den aktuellen Stand nach.
			if (x.wam.current !== props.id) {
				return;
			}
			const data = typeof args === 'string' ? JSON.parse(args) : args;
			if (data.price == null || data.timestamp == null) {
				return;
			}
			const time = new Date(data.timestamp).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
			setCharts((current) => {
				const index = current.findIndex((chart) => chart.symbol === data.symbol);
				if (index === -1) {
					// Symbol gehoert nicht zur aktuell angezeigten Watchlist (der
					// Kanal liefert Updates fuer ALLE Watchlists, siehe
					// marketdataWorker.mjs) - ignorieren.
					return current;
				}
				const chart = current[index];
				const candles = chart.candles || [];
				const lastCandle = candles[candles.length - 1];
				if (lastCandle && lastCandle.time === time && lastCandle.close === data.price) {
					return current;
				}
				const updated = [...current];
				updated[index] = { ...chart, candles: [...candles, { time, close: data.price }] };
				return updated;
			});
		}

		const connection = x.was.webSocket;
		if (connection) {
			connection.on('marketdata-quote', handleQuote);
		}
		return () => {
			if (connection) {
				connection.off('marketdata-quote', handleQuote);
			}
		};
	}, [isLiveAppendable, x, props.id]);

	return (
		<Module
			id={props.id}
			x={x}
			classes="module-marketdata-charts"
			title={props.title}
			display={props.display}
			displayTitle="d-block"
			onShow={consumeForcedGroup}
			titleToolbar={
				<span className="module-table-title-toolbar">
					<MarketdataGroupSelector x={x} id={props.id} onSelect={setSelectedGroupId} forcedGroup={forcedGroup} />
					<MarketdataRangeSelector range={range} onChange={setRange} />
					<i
						className="bi bi-pencil module-icon"
						title="Watchlist bearbeiten"
						onClick={() => {
							// Verwaltung soll dieselbe Watchlist zeigen, die hier gerade
							// ausgewaehlt ist (siehe marketdataForcedGroup.js).
							setMarketdataForcedGroup(x, selectedGroupId);
							x.wam.activate('marketdata-watchlist');
						}}
					/>
					<i
						className={'bi bi-arrow-repeat module-icon' + (loading ? ' marketdata-reload-spinning' : '')}
						title="Watchlist-Änderungen laden"
						onClick={loadCharts}
					/>
				</span>
			}
		>
			{loading && charts.length === 0 && <div className="marketdata-detail-hint">Charts werden geladen…</div>}
			{!loading && error && <div className="marketdata-detail-hint">{error}</div>}
			{!loading && !error && charts.length === 0 &&
				<div className="marketdata-empty">Keine Symbole in dieser Watchlist.</div>
			}
			{charts.length > 0 &&
				<div className="marketdata-charts-grid">
					{charts.map((chart) => (
						<MarketdataChartTile key={chart.symbol} symbol={chart.symbol} shortlabel={chart.shortlabel} candles={chart.candles} range={range} />
					))}
				</div>
			}
		</Module>
	)

};
